// StoreIssueNoteAdd.jsx
// ✅ New standardized design
// ✅ Add mode preserved
// ✅ Edit mode preserved
// ✅ SessionStorage edit flow preserved
// ✅ Existing APIs preserved
// ✅ Item search preserved
// ✅ Existing grid merge preserved
// ✅ Department / Vehicle preserved
// ✅ Validation preserved
// ✅ Submit payload preserved

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";

import {
  FaArrowLeft,
  FaSearch,
  FaPlus,
  FaTrash,
  FaSave,
  FaCog,
  FaClipboardList,
  FaWarehouse,
  FaFilter,
  FaBoxes,
  FaInfoCircle,
  FaBuilding,
  FaCar,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";

/* =========================================================
   API
========================================================= */

const API =
  "https://nissi-india.com/api/storeIssueNote";

/* =========================================================
   HELPERS
========================================================= */

const todayStr = () =>
  new Date()
    .toISOString()
    .split("T")[0];

const toStr = (value) =>
  String(value ?? "").trim();

/* =========================================================
   NORMALIZE SEARCH ITEMS
========================================================= */

const normalizeSearchItems = (
  rawItems = []
) => {
  return rawItems
    .map((row) => {
      const display =
        typeof row ===
        "string"
          ? row
          : row?.display ||
            row?.itemname_itemcode ||
            "";

      const itemcode =
        (typeof row ===
          "object" &&
          row?.itemcode) ||
        (display
          ? display
              .split("-")[0]
              .trim()
          : "");

      return {
        display,
        itemcode,

        itemid:
          typeof row ===
          "object"
            ? row?.itemid ??
              null
            : null,
      };
    })
    .filter(
      (item) =>
        item.itemcode
    );
};

/* =========================================================
   MAP DB ROW
========================================================= */

const mapDbRowToGridRow = (
  row
) => ({
  fld_itemid:
    row.fld_itemid ??
    null,

  fld_itemcode:
    row.fld_itemcode ??
    row.itemcode ??
    "",

  fld_itemname:
    row.fld_itemname ??
    row.itemname ??
    "",

  availableQty:
    Number(
      row.fld_availableqty ??
        row.fld_availableQty ??
        row.fld_closing_stock ??
        row.fld_closingstock ??
        0
    ),

  requestQty:
    Number(
      row.fld_requestqty ??
        row.fld_requestedqty ??
        0
    ),

  issueQty:
    Number(
      row.fld_issueqty ??
        row.IssuedQty ??
        row.fld_qty ??
        row.fld_Qty ??
        row.fld_qtyissued ??
        0
    ),

  departmentCode:
    row.fld_DepartmentCode ??
    row.fld_departmentcode ??
    "",

  departmentName:
    row.fld_DepartmentName ??
    row.fld_departmentname ??
    "",

  vehicleCode:
    row.fld_VehicleCode ??
    row.fld_vehichlecode ??
    "",

  vehicleName:
    row.fld_VehicleName ??
    row.fld_vehichlename ??
    "",

  supplierCode:
    row.fld_suppliercode ??
    "",

  supplierName:
    row.fld_suppliername ??
    "",

  tyreBatterySerialNo:
    row.tyreBatterySerialNo ??
    row.fld_tyrebatteryserialno ??
    "",

  tyreBatteryMake:
    row.tyreBatteryMake ??
    row.fld_tyrebatterymake ??
    "",

  srcSerialNo:
    row.srcSerialNo ??
    row.fld_srcserialno ??
    "",

  hsdfillinghrkm:
    String(
      row.fld_hsdfillinghrkm ??
        "0"
    ),

  hsdfillinghrkm1:
    String(
      row.fld_hsdfillingstartinghrkm ??
        row.prevHsdHrKm ??
        "0"
    ),

  isFuelCategory:
    Boolean(
      row.isFuelCategory
    ),

  detailId:
    row.fld_storesissuedetid
      ? Number(
          row.fld_storesissuedetid
        )
      : null,
});

/* =========================================================
   COMPONENT
========================================================= */

const StoreIssueNoteAdd =
  () => {
    const navigate =
      useNavigate();

    /* =======================================================
       THEME
    ======================================================= */

    const {
      isDark,
      skin,
      setSkin,
      theme,
    } = useContext(
      ThemeContext
    );

    const currentSkin =
      useMemo(
        () =>
          getCurrentSkin(
            theme ||
              skin
          ),
        [
          theme,
          skin,
        ]
      );

    const baseColor =
      currentSkin
        ?.baseColor ||
      "blue";

    const baseShade =
      currentSkin
        ?.baseShade ||
      "600";

    /* =======================================================
       LOCAL STORAGE
    ======================================================= */

    const finYearId =
      localStorage.getItem(
        "finYearId"
      ) || "";

    const finYear =
      localStorage.getItem(
        "finYear"
      ) || "";

    const branchId =
      parseInt(
        localStorage.getItem(
          "branchId"
        ) ||
          localStorage.getItem(
            "branchid"
          ) ||
          "0",
        10
      );

    const branch =
      localStorage.getItem(
        "branchType"
      ) ||
      localStorage.getItem(
        "branchtype"
      ) ||
      "";

    const loginUserId =
      parseInt(
        localStorage.getItem(
          "userId"
        ) ||
          localStorage.getItem(
            "loginuserid"
          ) ||
          "0",
        10
      );

    /* =======================================================
       HEADERS
    ======================================================= */

    const commonHeaders =
      useMemo(
        () => ({
          "x-finyearid":
            finYearId,

          "x-finyear":
            finYear,

          "x-branchid":
            branchId,

          "x-branch":
            branch,

          "x-userid":
            loginUserId,
        }),
        [
          finYearId,
          finYear,
          branchId,
          branch,
          loginUserId,
        ]
      );

    /* =======================================================
       SESSION EDIT VALUES
    ======================================================= */

    const existingStoreIssueNo =
      sessionStorage.getItem(
        "storeid"
      ) || null;

    const existingUserId =
      sessionStorage.getItem(
        "storeuserid"
      ) || null;

    const existingIssueId =
      sessionStorage.getItem(
        "storeissueid"
      ) || null;

    const isEditMode =
      Boolean(
        existingIssueId
      );

    /* =======================================================
       DESIGN CLASSES
    ======================================================= */

    const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }`;

    const readOnlyInputClass = `w-full px-3 py-2.5 rounded-lg border text-sm cursor-not-allowed ${
      isDark
        ? "bg-slate-900/40 border-slate-700 text-slate-400"
        : "bg-slate-100 border-slate-200 text-slate-600"
    }`;

    const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }`;

    const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
      isDark
        ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
        : `bg-white ${
            currentSkin?.glass ||
            ""
          } border-gray-200`
    }`;

    const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
      isDark
        ? "border-slate-600 text-slate-300 hover:bg-slate-800"
        : "border-slate-300 text-slate-700 hover:bg-slate-100"
    }`;

    const tableRowClass = `transition-colors ${
      isDark
        ? "bg-gray-800/20 hover:bg-gray-700/30"
        : "bg-white hover:bg-gray-50"
    }`;

    const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
      isDark
        ? "text-gray-300"
        : "text-gray-900"
    }`;

    /* =======================================================
       STATE
    ======================================================= */

    const [
      initializing,
      setInitializing,
    ] = useState(true);

    const [
      storeIssueNo,
      setStoreIssueNo,
    ] = useState("");

    const [
      issueDate,
      setIssueDate,
    ] = useState(
      todayStr()
    );

    const [
      currentStore,
      setCurrentStore,
    ] = useState("");

    const [
      storeList,
      setStoreList,
    ] = useState([]);

    const [
      remarks,
      setRemarks,
    ] = useState("");

    const [approve] =
      useState(true);

    /* =======================================================
       RADIO VALUES
    ======================================================= */

    const [
      issueType,
      setIssueType,
    ] = useState("FS");

    const [
      issueFor,
      setIssueFor,
    ] = useState("D");

    const [
      vehicleType,
      setVehicleType,
    ] = useState("O");

    /* =======================================================
       DROPDOWNS
    ======================================================= */

    const [
      itemCategories,
      setItemCategories,
    ] = useState([]);

    const [
      storeReqGrid,
      setStoreReqGrid,
    ] = useState([]);

    const [
      projectList,
    ] = useState([]);

    const [
      productionList,
      setProductionList,
    ] = useState([]);

    // Own / Hire vehicle or Department options for the grid select boxes
    const [
      codeOptions,
      setCodeOptions,
    ] = useState([]);

    /* =======================================================
       FILTERS
    ======================================================= */

    const [
      itemCategory,
      setItemCategory,
    ] = useState("");

    const [
      itemName,
      setItemName,
    ] = useState("");

    const [
      itemCode,
      setItemCode,
    ] = useState("");

    const [
      projectId,
      setProjectId,
    ] = useState("");

    const [
      productionId,
      setProductionId,
    ] = useState("");

    /* =======================================================
       SEARCH / SELECTED
    ======================================================= */

    const [
      searchResults,
      setSearchResults,
    ] = useState([]);

    const [
      selectedItems,
      setSelectedItems,
    ] = useState([]);

    /* =======================================================
       GRID
    ======================================================= */

    const [
      gridItems,
      setGridItems,
    ] = useState([]);

    /* =======================================================
       FLAGS
    ======================================================= */

    const [
      saving,
      setSaving,
    ] = useState(false);

    const [
      searching,
      setSearching,
    ] = useState(false);

    const [
      addingItems,
      setAddingItems,
    ] = useState(false);

    /* =======================================================
       INITIAL LOAD GUARD
    ======================================================= */

    const initializedRef =
      useRef(false);

    /* =======================================================
       LOAD ITEM CATEGORIES
    ======================================================= */

    const loadItemCategories =
      useCallback(
        async () => {
          try {
            const response =
              await axios.get(
                `${API}/storeIssue_itemcategories`,
                {
                  headers:
                    commonHeaders,
                }
              );

            setItemCategories(
              Array.isArray(
                response.data
                  ?.categories
              )
                ? response.data
                    .categories
                : []
            );
          } catch (error) {
            console.error(
              "Item category load error:",
              error
            );

            toast.error(
              "Failed to load item categories"
            );
          }
        },
        [
          commonHeaders,
        ]
      );

    /* =======================================================
       LOAD STORES
    ======================================================= */

    const loadStores =
      useCallback(
        async () => {
          if (
            !branchId
          ) {
            toast.error(
              "Branch ID not found in localStorage"
            );

            return [];
          }

          try {
            const response =
              await axios.get(
                `${API}/storeIssue_storesFetch`,
                {
                  params: {
                    branchid:
                      branchId,
                  },

                  headers:
                    commonHeaders,
                }
              );

            const stores =
              Array.isArray(
                response.data
                  ?.stores
              )
                ? response.data
                    .stores
                : [];

            setStoreList(
              stores
            );

            return stores;
          } catch (error) {
            console.error(
              "Store load error:",
              error
            );

            toast.error(
              "Failed to load stores"
            );

            return [];
          }
        },
        [
          branchId,
          commonHeaders,
        ]
      );

    /* =======================================================
       GENERATE ISSUE NO
    ======================================================= */

    const generateIssueNo =
      useCallback(
        async () => {
          if (
            !loginUserId
          ) {
            toast.error(
              "User ID not found in localStorage"
            );

            return;
          }

          try {
            const response =
              await axios.post(
                `${API}/storeIssue_generateNo`,
                {
                  userId:
                    loginUserId,
                },
                {
                  headers:
                    commonHeaders,
                }
              );

            setStoreIssueNo(
              response.data
                ?.storeIssueNo ||
                ""
            );
          } catch (error) {
            console.error(
              "Generate issue number error:",
              error
            );

            toast.error(
              "Failed to generate issue no"
            );
          }
        },
        [
          loginUserId,
          commonHeaders,
        ]
      );

    /* =======================================================
       LOAD STORE REQUEST GRID
    ======================================================= */

    const loadStoreReqGrid =
      useCallback(
        async () => {
          try {
            const response =
              await axios.get(
                `${API}/storeIssue_addList`,
                {
                  params: {
                    branchId,
                  },

                  headers:
                    commonHeaders,
                }
              );

            setStoreReqGrid(
              Array.isArray(
                response.data
                  ?.data
              )
                ? response.data
                    .data
                : []
            );
          } catch (error) {
            console.error(
              "Store request load error:",
              error
            );

            toast.error(
              "Failed to load store requests"
            );
          }
        },
        [
          branchId,
          commonHeaders,
        ]
      );

    /* =======================================================
       LOAD PRODUCTIONS
    ======================================================= */

    const loadProductions =
      useCallback(
        async (
          projId
        ) => {
          try {
            const response =
              await axios.get(
                `${API}/storeIssue_productions`,
                {
                  params: {
                    projectId:
                      projId,
                  },

                  headers:
                    commonHeaders,
                }
              );

            setProductionList(
              Array.isArray(
                response.data
                  ?.productions
              )
                ? response.data
                    .productions
                : []
            );
          } catch (error) {
            console.error(
              "Production load error:",
              error
            );

            toast.error(
              "Failed to load productions"
            );
          }
        },
        [
          commonHeaders,
        ]
      );

    /* =======================================================
       LOAD EXISTING EDIT DATA
    ======================================================= */

    const loadExistingDataFromSession =
      useCallback(
        async () => {
          try {
            if (
              !existingStoreIssueNo ||
              !existingIssueId
            ) {
              toast.error(
                "Edit session missing storeid or storeissueid"
              );

              return;
            }

            const headerRes =
              await axios.get(
                `${API}/storeIssue_existingDetails`,
                {
                  params: {
                    storeIssueNo:
                      existingStoreIssueNo,

                    storeIssueId:
                      existingIssueId,
                  },

                  headers:
                    commonHeaders,
                }
              );

            if (
              !headerRes.data
                ?.success
            ) {
              toast.error(
                headerRes.data
                  ?.message ||
                  "Failed to load issue header"
              );

              return;
            }

            const header =
              headerRes.data
                ?.data ||
              {};

            const resolvedStoreId =
              header.storeId ||
              existingUserId ||
              "";

            setStoreIssueNo(
              existingStoreIssueNo
            );

            setIssueDate(
              header.dateIssued
                ? header.dateIssued.split(
                    "T"
                  )[0]
                : todayStr()
            );

            setCurrentStore(
              String(
                resolvedStoreId
              )
            );

            setRemarks(
              header.remarks ||
                ""
            );

            setIssueType(
              header.issueType ||
                "FS"
            );

            setIssueFor(
              header.issueFor ||
                "D"
            );

            setVehicleType(
              header.vehicleType ||
                "O"
            );

            if (
              !resolvedStoreId
            ) {
              toast.warning(
                "Could not resolve storeId — detail items may not load"
              );

              return;
            }

            const detailRes =
              await axios.get(
                `${API}/storeIssue_updateItems`,
                {
                  params: {
                    branchId,

                    storeId:
                      resolvedStoreId,

                    storeIssueId:
                      existingIssueId,

                    isDepartment:
                      header.issueFor ===
                      "D",

                    isVehicle:
                      header.issueFor ===
                      "V",

                    isSparesReturn:
                      header.issueFor ===
                      "S",

                    isProject:
                      header.issueFor ===
                      "P",
                  },

                  headers:
                    commonHeaders,
                }
              );

            if (
              !detailRes.data
                ?.success
            ) {
              toast.error(
                detailRes.data
                  ?.message ||
                  "Failed to load issue items"
              );

              return;
            }

            const rows =
              Array.isArray(
                detailRes.data
                  ?.data
              )
                ? detailRes.data
                    .data
                : [];

            const mapped =
              rows.map(
                mapDbRowToGridRow
              );

            setGridItems(
              mapped
            );

            setSelectedItems(
              mapped
                .map(
                  (item) =>
                    item.fld_itemcode
                )
                .filter(
                  Boolean
                )
            );
          } catch (error) {
            console.error(
              "Edit load error:",
              error
            );

            toast.error(
              "Failed to load existing issue in edit mode"
            );
          }
        },
        [
          branchId,
          commonHeaders,
          existingIssueId,
          existingStoreIssueNo,
          existingUserId,
        ]
      );

    /* =======================================================
       SAFE INITIAL LOAD
    ======================================================= */

    useEffect(() => {
      if (
        initializedRef.current
      ) {
        return;
      }

      initializedRef.current =
        true;

      const initialize =
        async () => {
          setInitializing(
            true
          );

          try {
            const [
              stores,
            ] =
              await Promise.all([
                loadStores(),
                loadItemCategories(),
              ]);

            if (
              !isEditMode &&
              stores?.length >
                0
            ) {
              const firstStore =
                stores.find(
                  (store) =>
                    store.fld_storeid
                );

              if (
                firstStore
              ) {
                setCurrentStore(
                  String(
                    firstStore.fld_storeid
                  )
                );
              }
            }

            if (
              isEditMode
            ) {
              await loadExistingDataFromSession();
            } else {
              await generateIssueNo();
            }
          } finally {
            setInitializing(
              false
            );
          }
        };

      initialize();
    }, [
      generateIssueNo,
      isEditMode,
      loadExistingDataFromSession,
      loadItemCategories,
      loadStores,
    ]);

    /* =======================================================
       ISSUE TYPE
    ======================================================= */

    useEffect(() => {
      if (
        issueType ===
        "FR"
      ) {
        loadStoreReqGrid();
      }
    }, [
      issueType,
      loadStoreReqGrid,
    ]);

    /* =======================================================
       VEHICLE / DEPARTMENT OPTIONS
       D → departments, V + O → own vehicles, V + H → hire vehicles
    ======================================================= */

    useEffect(() => {
      if (!branchId) {
        return;
      }

      let cancelled = false;
      const type = issueFor === "D" ? "D" : vehicleType;

      const loadCodeOptions = async () => {
        try {
          const response = await axios.get(
            `${API}/storeIssue_codeOptions`,
            {
              params: { branchId, type },
              headers: commonHeaders,
            }
          );

          if (!cancelled) {
            setCodeOptions(
              Array.isArray(response.data?.options)
                ? response.data.options
                : []
            );
          }
        } catch (error) {
          console.error("Code options load error:", error);

          if (!cancelled) {
            setCodeOptions([]);
            toast.error(
              issueFor === "D"
                ? "Failed to load departments"
                : "Failed to load vehicles"
            );
          }
        }
      };

      loadCodeOptions();

      return () => {
        cancelled = true;
      };
    }, [issueFor, vehicleType, branchId, commonHeaders]);

    /* =======================================================
       PROJECT PRODUCTION
    ======================================================= */

    useEffect(() => {
      if (
        projectId &&
        projectId !==
          "Select"
      ) {
        loadProductions(
          projectId
        );
      }
    }, [
      projectId,
      loadProductions,
    ]);

    /* =======================================================
       SEARCH ITEMS
    ======================================================= */

    const handleSearchItems =
      async () => {
        if (
          !itemCategory
        ) {
          toast.warning(
            "Please select an Item Category"
          );

          return;
        }

        if (
          !branchId
        ) {
          toast.warning(
            "Branch ID not found in localStorage"
          );

          return;
        }

        if (
          !currentStore
        ) {
          toast.warning(
            "Please select a Store"
          );

          return;
        }

        setSearching(
          true
        );

        try {
          const response =
            await axios.get(
              `${API}/storeIssue_searchItems`,
              {
                params: {
                  itemCategory,

                  itemCode,

                  itemName,

                  branchId,

                  currentStore,

                  issueDate,
                },

                headers:
                  commonHeaders,
              }
            );

          const normalized =
            normalizeSearchItems(
              response.data
                ?.items ||
                []
            );

          setSearchResults(
            normalized
          );

          const gridCodes =
            gridItems
              .map(
                (item) =>
                  item.fld_itemcode
              )
              .filter(
                Boolean
              );

          setSelectedItems(
            (previous) =>
              Array.from(
                new Set([
                  ...previous,
                  ...gridCodes,
                ])
              )
          );

          if (
            normalized.length ===
            0
          ) {
            toast.info(
              "No items found"
            );
          } else {
            toast.success(
              `Found ${normalized.length} item(s)`
            );
          }
        } catch (error) {
          console.error(
            "Item search error:",
            error
          );

          toast.error(
            "Item search failed"
          );
        } finally {
          setSearching(
            false
          );
        }
      };

    /* =======================================================
       SEARCH ITEM SELECT
    ======================================================= */

    const toggleSearchItem =
      (
        item
      ) => {
        if (
          !item?.itemcode
        ) {
          return;
        }

        setSelectedItems(
          (previous) =>
            previous.includes(
              item.itemcode
            )
              ? previous.filter(
                  (code) =>
                    code !==
                    item.itemcode
                )
              : [
                  ...previous,
                  item.itemcode,
                ]
        );
      };

    /* =======================================================
       ADD ITEMS
    ======================================================= */

    const handleAddItems =
      async () => {
        if (
          selectedItems.length ===
          0
        ) {
          toast.warning(
            "Please select at least one item"
          );

          return;
        }

        if (
          !currentStore
        ) {
          toast.warning(
            "Please select a Store"
          );

          return;
        }

        setAddingItems(
          true
        );

        try {
          let response;

          if (
            gridItems.length ===
            0
          ) {
            response =
              await axios.post(
                `${API}/storeIssue_fillitemsgrid`,
                {
                  branchid:
                    branchId,

                  storeid:
                    parseInt(
                      currentStore,
                      10
                    ),

                  selectid:
                    selectedItems.join(
                      ","
                    ),

                  isDepartment:
                    issueFor ===
                    "D",
                },
                {
                  headers:
                    commonHeaders,
                }
              );
          } else {
            const recoveritemcode =
              gridItems
                .map(
                  (item) =>
                    item.fld_itemcode
                )
                .join(",");

            const recoverreqqtyadd =
              gridItems
                .map(
                  (item) =>
                    item.requestQty ||
                    "0"
                )
                .join(",");

            const recoverissueqtyadd =
              gridItems
                .map(
                  (item) =>
                    item.issueQty ||
                    "0"
                )
                .join(",");

            response =
              await axios.post(
                `${API}/storeIssue_fillitemsandvaluegrid`,
                {
                  branchid:
                    branchId,

                  storeid:
                    parseInt(
                      currentStore,
                      10
                    ),

                  selectid:
                    selectedItems.join(
                      ","
                    ),

                  recoveritemcode,

                  recoverreqqtyadd,

                  recoverissueqtyadd,

                  isDepartment:
                    issueFor ===
                    "D",
                },
                {
                  headers:
                    commonHeaders,
                }
              );
          }

          if (
            response.data
              ?.success
          ) {
            const incoming =
              (
                response.data
                  ?.items ||
                []
              ).map(
                mapDbRowToGridRow
              );

            const oldMap =
              new Map(
                gridItems.map(
                  (item) => [
                    item.fld_itemcode,
                    item,
                  ]
                )
              );

            const merged =
              incoming.map(
                (row) => {
                  const old =
                    oldMap.get(
                      row.fld_itemcode
                    );

                  if (
                    !old
                  ) {
                    return row;
                  }

                  return {
                    ...row,

                    requestQty:
                      old.requestQty ??
                      row.requestQty,

                    issueQty:
                      old.issueQty ??
                      row.issueQty,

                    departmentCode:
                      old.departmentCode ??
                      row.departmentCode,

                    departmentName:
                      old.departmentName ??
                      row.departmentName,

                    vehicleCode:
                      old.vehicleCode ??
                      row.vehicleCode,

                    vehicleName:
                      old.vehicleName ??
                      row.vehicleName,

                    detailId:
                      old.detailId ??
                      row.detailId,
                  };
                }
              );

            setGridItems(
              merged
            );

            toast.success(
              "Items added/updated in grid"
            );
          }
        } catch (error) {
          console.error(
            "Add item error:",
            error
          );

          toast.error(
            "Failed to add items"
          );
        } finally {
          setAddingItems(
            false
          );
        }
      };

    /* =======================================================
       GRID UPDATE
    ======================================================= */

    const updateGridRow =
      (
        index,
        field,
        value
      ) => {
        setGridItems(
          (previous) =>
            previous.map(
              (
                row,
                currentIndex
              ) =>
                currentIndex ===
                index
                  ? {
                      ...row,
                      [field]:
                        value,
                    }
                  : row
            )
        );
      };

    /* =======================================================
       VEHICLE / DEPARTMENT SELECT HELPERS
    ======================================================= */

    const handleRowCodeSelect = (index, code) => {
      const selected = codeOptions.find(
        (option) => option.code === code
      );
      const name = selected?.name || "";

      setGridItems((previous) =>
        previous.map((row, currentIndex) => {
          if (currentIndex !== index) {
            return row;
          }

          return issueFor === "D"
            ? { ...row, departmentCode: code, departmentName: name }
            : { ...row, vehicleCode: code, vehicleName: name };
        })
      );
    };

    // Keeps an already-saved code visible in edit mode even if it
    // is no longer in the master list
    const renderCodeOptions = (currentCode, currentName) => {
      const list =
        currentCode &&
        !codeOptions.some((option) => option.code === currentCode)
          ? [{ code: currentCode, name: currentName }, ...codeOptions]
          : codeOptions;

      return list.map((option) => (
        <option key={option.code} value={option.code}>
          {option.name
            ? `${option.code} - ${option.name}`
            : option.code}
        </option>
      ));
    };

    // An own-vehicle code is invalid for hire (and vice versa), so clear row picks
    const clearRowCodes = () => {
      setGridItems((previous) =>
        previous.map((row) => ({
          ...row,
          departmentCode: "",
          departmentName: "",
          vehicleCode: "",
          vehicleName: "",
        }))
      );
    };

    const handleIssueForChange = (value) => {
      if (value === issueFor) return;
      setIssueFor(value);
      clearRowCodes();
    };

    const handleVehicleTypeChange = (value) => {
      if (value === vehicleType) return;
      setVehicleType(value);
      clearRowCodes();
    };

    /* =======================================================
       REMOVE GRID ROW
    ======================================================= */

    const removeGridRow =
      (index) => {
        const removed =
          gridItems[index];

        setGridItems(
          (previous) =>
            previous.filter(
              (
                _,
                currentIndex
              ) =>
                currentIndex !==
                index
            )
        );

        if (
          removed?.fld_itemcode
        ) {
          setSelectedItems(
            (previous) =>
              previous.filter(
                (code) =>
                  code !==
                  removed.fld_itemcode
              )
          );
        }
      };

    /* =======================================================
       CLEAR SEARCH FILTER
    ======================================================= */

    const clearSearchFilters =
      () => {
        setItemCategory(
          ""
        );

        setItemName(
          ""
        );

        setItemCode(
          ""
        );

        setSearchResults(
          []
        );
      };

    /* =======================================================
       VALIDATION
    ======================================================= */

    const validate =
      () => {
        if (
          !issueDate
        ) {
          toast.error(
            "Please select Issue Date"
          );

          return false;
        }

        if (
          !currentStore
        ) {
          toast.error(
            "Please select a Store"
          );

          return false;
        }

        if (
          gridItems.length ===
          0
        ) {
          toast.error(
            "Please add at least one item"
          );

          return false;
        }

        for (
          let index = 0;
          index <
          gridItems.length;
          index += 1
        ) {
          const row =
            gridItems[index];

          const qty =
            parseFloat(
              row.issueQty
            );

          if (
            !qty ||
            qty <= 0
          ) {
            toast.error(
              `Row ${
                index + 1
              }: Issue Qty must be greater than 0`
            );

            return false;
          }

          if (
            qty >
            parseFloat(
              row.availableQty ||
                0
            )
          ) {
            toast.error(
              `Row ${
                index + 1
              }: Issue Qty exceeds Available Qty`
            );

            return false;
          }

          if (
            issueFor ===
              "D" &&
            !String(
              row.departmentCode ||
                ""
            ).trim()
          ) {
            toast.error(
              `Row ${
                index + 1
              }: Please enter Department`
            );

            return false;
          }

          if (
            issueFor ===
              "V" &&
            !String(
              row.vehicleCode ||
                ""
            ).trim()
          ) {
            toast.error(
              `Row ${
                index + 1
              }: Please enter Vehicle Code`
            );

            return false;
          }
        }

        return true;
      };

    /* =======================================================
       SUBMIT
    ======================================================= */

    const handleSubmit =
      async () => {
        if (
          !validate()
        ) {
          return;
        }

        setSaving(
          true
        );

        try {
          const payload = {
            storeIssueId:
              isEditMode
                ? parseInt(
                    existingIssueId,
                    10
                  )
                : null,

            storeIssueNo,

            issueDate,

            remarks,

            loginUserId,

            branchId,

            storeId:
              parseInt(
                currentStore,
                10
              ),

            issueType,

            vehicleType,

            issueFor,

            approve: true,

            storeReqId: 0,

            vehicleHigher:
              vehicleType ===
              "H",

            items:
              gridItems.map(
                (row) => ({
                  itemId:
                    row.fld_itemid,

                  issueQty:
                    parseFloat(
                      row.issueQty
                    ),

                  requestQty:
                    parseFloat(
                      row.requestQty ||
                        0
                    ),

                  availableQty:
                    parseFloat(
                      row.availableQty ||
                        0
                    ),

                  departmentCode:
                    row.departmentCode ||
                    "",

                  departmentName:
                    row.departmentName ||
                    "",

                  vehicleCode:
                    row.vehicleCode ||
                    "",

                  vehicleName:
                    row.vehicleName ||
                    "",

                  supplierCode:
                    row.supplierCode ||
                    "",

                  supplierName:
                    row.supplierName ||
                    "",

                  tyreBatterySerialNo:
                    row.tyreBatterySerialNo ||
                    "",

                  tyreBatteryMake:
                    row.tyreBatteryMake ||
                    "",

                  srcSerialNo:
                    row.srcSerialNo ||
                    "",

                  hsdfillinghrkm:
                    row.hsdfillinghrkm ||
                    "0",

                  hsdfillinghrkm1:
                    row.hsdfillinghrkm1 ||
                    "0",

                  detailId:
                    row.detailId ||
                    null,
                })
              ),
          };

          const response =
            await axios.post(
              `${API}/storeIssue_submit`,
              payload,
              {
                headers:
                  commonHeaders,
              }
            );

          if (
            response.data
              ?.success
          ) {
            toast.success(
              response.data
                ?.message ||
                "Saved successfully"
            );

            sessionStorage.removeItem(
              "storeid"
            );

            sessionStorage.removeItem(
              "storeuserid"
            );

            sessionStorage.removeItem(
              "storeissueid"
            );

            navigate(
              "/inventory/StoreRequisition_Listing"
            );
          } else {
            toast.error(
              response.data
                ?.message ||
                "Save failed"
            );
          }
        } catch (error) {
          console.error(
            "Save Store Issue error:",
            error
          );

          toast.error(
            error.response
              ?.data
              ?.message ||
              "Error saving store issue"
          );
        } finally {
          setSaving(
            false
          );
        }
      };

    /* =======================================================
       TOTAL ISSUE QTY
    ======================================================= */

    const totalIssueQty =
      useMemo(
        () =>
          gridItems
            .reduce(
              (
                sum,
                row
              ) =>
                sum +
                (parseFloat(
                  row.issueQty
                ) ||
                  0),
              0
            )
            .toFixed(2),
        [
          gridItems,
        ]
      );

    /* =======================================================
       RENDER
    ======================================================= */

    return (
      <LayoutWrapper>
        {/* ===================================================
            HEADER BAR
        =================================================== */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              {isEditMode
                ? "EDIT STORE ISSUE NOTE"
                : "ADD STORE ISSUE NOTE"}
            </span>
          }
          setSkin={
            setSkin
          }
          currentSkin={
            skin
          }
        />

        <div className="space-y-6 mt-6">
          {/* =================================================
              PAGE HEADER
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            <div
              className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaClipboardList className="w-5 h-5 text-white" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      {isEditMode
                        ? "Update Store Issue Note"
                        : "New Store Issue Note"}
                    </h3>

                    {storeIssueNo && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                            : `bg-${baseColor}-50 text-${baseColor}-700`
                        }`}
                      >
                        Issue No:{" "}
                        {
                          storeIssueNo
                        }
                      </span>
                    )}

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-emerald-900/30 text-emerald-300"
                          : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {
                        gridItems.length
                      }{" "}
                      Item(s)
                    </span>

                    {(initializing ||
                      saving) && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <FaCog className="animate-spin" />

                        {saving
                          ? "Saving..."
                          : "Loading..."}
                      </span>
                    )}
                  </div>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Create and manage
                    stock issue details
                    for departments and
                    vehicles
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      "/inventory/StoreRequisition_Listing"
                    )
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaArrowLeft />

                  Back
                </button>

                <button
                  type="button"
                  onClick={
                    handleSubmit
                  }
                  disabled={
                    saving ||
                    initializing
                  }
                  className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {saving ? (
                    <FaCog className="animate-spin" />
                  ) : (
                    <FaSave />
                  )}

                  {saving
                    ? "Saving..."
                    : isEditMode
                    ? "Update"
                    : "Save"}
                </button>
              </div>
            </div>
          </div>

          {/* =================================================
              INITIAL LOADING
          ================================================= */}

          {initializing ? (
            <div
              className={
                cardClass
              }
            >
              <div className="py-20 text-center">
                <FaCog
                  className={`w-10 h-10 mx-auto animate-spin text-${baseColor}-500`}
                />

                <p
                  className={`mt-3 text-sm ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-600"
                  }`}
                >
                  Loading Store Issue
                  details...
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* =============================================
                  ISSUE DETAILS
              ============================================= */}

              <div
                className={
                  cardClass
                }
              >
                {/* HEADER */}

                <div
                  className={`px-6 py-4 border-b ${
                    isDark
                      ? "border-slate-700/50 bg-slate-900/40"
                      : "border-slate-200/50 bg-white/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                    >
                      <FaWarehouse className="text-white" />
                    </div>

                    <div>
                      <h3
                        className={`text-lg font-bold ${
                          isDark
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        Issue Details
                      </h3>

                      <p
                        className={`text-xs ${
                          isDark
                            ? "text-slate-400"
                            : "text-slate-600"
                        }`}
                      >
                        Enter issue
                        number, date,
                        store and issue
                        type details
                      </p>
                    </div>
                  </div>
                </div>

                {/* BODY */}

                <div className="p-6">
                  {/* ISSUE TYPE */}

                  <div
                    className={`p-5 rounded-xl border mb-6 ${
                      isDark
                        ? "bg-slate-800/30 border-slate-700"
                        : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <label
                      className={
                        labelClass
                      }
                    >
                      Store Issue Type
                    </label>

                    <div className="flex flex-wrap gap-6 mt-3">
                      <label
                        className={`inline-flex items-center gap-2 text-sm cursor-pointer ${
                          isDark
                            ? "text-gray-300"
                            : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={
                            issueType ===
                            "FS"
                          }
                          disabled={
                            isEditMode
                          }
                          onChange={() =>
                            setIssueType(
                              "FS"
                            )
                          }
                          className={`accent-${baseColor}-600`}
                        />

                        From Store
                      </label>

                      <label
                        className={`inline-flex items-center gap-2 text-sm cursor-pointer ${
                          isDark
                            ? "text-gray-300"
                            : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={
                            issueType ===
                            "FR"
                          }
                          disabled={
                            isEditMode
                          }
                          onChange={() =>
                            setIssueType(
                              "FR"
                            )
                          }
                          className={`accent-${baseColor}-600`}
                        />

                        From Store
                        Request
                      </label>
                    </div>
                  </div>

                  {/* FORM */}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {/* ISSUE NO */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Store Issue No
                      </label>

                      <input
                        type="text"
                        value={
                          storeIssueNo
                        }
                        readOnly
                        className={
                          readOnlyInputClass
                        }
                      />
                    </div>

                    {/* DATE */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Date

                        <span className="text-red-500 ml-1">
                          *
                        </span>
                      </label>

                      <input
                        type="date"
                        value={
                          issueDate
                        }
                        max={
                          todayStr()
                        }
                        onChange={(
                          event
                        ) =>
                          setIssueDate(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                      />
                    </div>

                    {/* STORE */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        From Store

                        <span className="text-red-500 ml-1">
                          *
                        </span>
                      </label>

                      <select
                        value={
                          currentStore
                        }
                        onChange={(
                          event
                        ) =>
                          setCurrentStore(
                            event
                              .target
                              .value
                          )
                        }
                        disabled={
                          isEditMode
                        }
                        className={
                          inputClass
                        }
                      >
                        <option value="">
                          Select
                        </option>

                        {storeList.map(
                          (
                            store,
                            index
                          ) => (
                            <option
                              key={
                                store.fld_storeid ||
                                index
                              }
                              value={
                                store.fld_storeid
                              }
                            >
                              {
                                store.fld_storename
                              }
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    {/* REMARKS */}

                    <div className="md:col-span-2 lg:col-span-2">
                      <label
                        className={
                          labelClass
                        }
                      >
                        Remarks
                      </label>

                      <input
                        type="text"
                        value={
                          remarks
                        }
                        onChange={(
                          event
                        ) =>
                          setRemarks(
                            event
                              .target
                              .value
                          )
                        }
                        placeholder="Optional remarks"
                        className={
                          inputClass
                        }
                      />
                    </div>
                  </div>

                  {/* =========================================
                      ISSUE FOR
                  ========================================= */}

                  <div
                    className={`mt-6 p-5 rounded-xl border ${
                      isDark
                        ? "bg-slate-800/30 border-slate-700"
                        : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <label
                      className={
                        labelClass
                      }
                    >
                      Issue For
                    </label>

                    <div className="flex flex-wrap gap-6 mt-3">
                      {/* DEPARTMENT */}

                      <label
                        className={`inline-flex items-center gap-2 cursor-pointer text-sm font-medium ${
                          isDark
                            ? "text-gray-300"
                            : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={
                            issueFor ===
                            "D"
                          }
                                                  onChange={() =>
                            handleIssueForChange("D")
                          }
                          className={`accent-${baseColor}-600`}
                        />

                        <FaBuilding />

                        Department
                      </label>

                      {/* VEHICLE */}

                      <label
                        className={`inline-flex items-center gap-2 cursor-pointer text-sm font-medium ${
                          isDark
                            ? "text-gray-300"
                            : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={
                            issueFor ===
                            "V"
                          }
                                                   onChange={() =>
                            handleIssueForChange("V")
                          }
                          className={`accent-${baseColor}-600`}
                        />

                        <FaCar />

                        Vehicle
                      </label>
                    </div>

                    {/* VEHICLE TYPE */}

                    {issueFor ===
                      "V" && (
                      <div
                        className={`mt-4 p-4 rounded-lg ${
                          isDark
                            ? "bg-slate-900/30"
                            : "bg-white"
                        }`}
                      >
                        <span
                          className={`block text-xs font-semibold mb-3 ${
                            isDark
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          Vehicle Type
                        </span>

                        <div className="flex flex-wrap gap-6">
                          <label
                            className={`inline-flex items-center gap-2 cursor-pointer text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            <input
                              type="radio"
                              checked={
                                vehicleType ===
                                "O"
                              }
                              onChange={() =>
                                handleVehicleTypeChange("O")
                              }
                              className={`accent-${baseColor}-600`}
                            />

                            Own
                          </label>

                          <label
                            className={`inline-flex items-center gap-2 cursor-pointer text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            <input
                              type="radio"
                              checked={
                                vehicleType ===
                                "H"
                              }
                              onChange={() =>
                                handleVehicleTypeChange("H")
                              }
                              className={`accent-${baseColor}-600`}
                            />

                            Hire
                          </label>
                        </div>
                      </div>
                    )}

                    {/* APPROVE */}

                    <div
                      className={`mt-5 pt-4 border-t ${
                        isDark
                          ? "border-slate-700"
                          : "border-slate-200"
                      }`}
                    >
                      <label className="inline-flex items-center gap-2 cursor-not-allowed opacity-70">
                        <input
                          type="checkbox"
                          checked={
                            approve
                          }
                          readOnly
                          className={`w-5 h-5 accent-${baseColor}-600`}
                        />

                        <FaCheckCircle className="text-emerald-500" />

                        <span
                          className={`text-sm font-medium ${
                            isDark
                              ? "text-gray-300"
                              : "text-gray-700"
                          }`}
                        >
                          Approve
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* =============================================
                  FILTER AREA
              ============================================= */}

              {!isEditMode && (
                <div
                  className={
                    cardClass
                  }
                >
                  {/* HEADER */}

                  <div
                    className={`px-6 py-4 border-b ${
                      isDark
                        ? "border-slate-700/50 bg-slate-900/40"
                        : "border-slate-200/50 bg-white/60"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                      >
                        <FaFilter className="text-white" />
                      </div>

                      <div>
                        <h3
                          className={`text-lg font-bold ${
                            isDark
                              ? "text-white"
                              : "text-slate-900"
                          }`}
                        >
                          Filtration
                          Area
                        </h3>

                        <p
                          className={`text-xs ${
                            isDark
                              ? "text-slate-400"
                              : "text-slate-600"
                          }`}
                        >
                          Search and
                          select items
                          for this issue
                          note
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* BODY */}

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {/* CATEGORY */}

                      <div>
                        <label
                          className={
                            labelClass
                          }
                        >
                          Item Category
                        </label>

                        <select
                          value={
                            itemCategory
                          }
                          onChange={(
                            event
                          ) =>
                            setItemCategory(
                              event
                                .target
                                .value
                            )
                          }
                          className={
                            inputClass
                          }
                        >
                          <option value="">
                            Select
                          </option>

                          {itemCategories.map(
                            (
                              category,
                              index
                            ) => (
                              <option
                                key={
                                  category.fld_itemcategoryid ||
                                  index
                                }
                                value={
                                  category.fld_itemcategoryid
                                }
                              >
                                {
                                  category.fld_itemcategory_name
                                }
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      {/* ITEM NAME */}

                      <div>
                        <label
                          className={
                            labelClass
                          }
                        >
                          Item Name
                        </label>

                        <input
                          type="text"
                          value={
                            itemName
                          }
                          onChange={(
                            event
                          ) =>
                            setItemName(
                              event
                                .target
                                .value
                            )
                          }
                          onKeyDown={(
                            event
                          ) => {
                            if (
                              event.key ===
                              "Enter"
                            ) {
                              handleSearchItems();
                            }
                          }}
                          placeholder="Enter Item Name"
                          className={
                            inputClass
                          }
                        />
                      </div>

                      {/* ITEM CODE */}

                      <div>
                        <label
                          className={
                            labelClass
                          }
                        >
                          Item Code
                        </label>

                        <input
                          type="text"
                          value={
                            itemCode
                          }
                          onChange={(
                            event
                          ) =>
                            setItemCode(
                              event
                                .target
                                .value
                            )
                          }
                          onKeyDown={(
                            event
                          ) => {
                            if (
                              event.key ===
                              "Enter"
                            ) {
                              handleSearchItems();
                            }
                          }}
                          placeholder="Enter Item Code"
                          className={
                            inputClass
                          }
                        />
                      </div>
                    </div>

                    {/* ACTIONS */}

                    <div
                      className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                        isDark
                          ? "border-slate-700"
                          : "border-slate-200"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={
                          clearSearchFilters
                        }
                        className={
                          neutralButtonClass
                        }
                      >
                        <FaTimes />

                        Clear Filters
                      </button>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={
                            handleSearchItems
                          }
                          disabled={
                            searching
                          }
                          className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60`}
                        >
                          {searching ? (
                            <FaCog className="animate-spin" />
                          ) : (
                            <FaSearch />
                          )}

                          {searching
                            ? "Searching..."
                            : "Search Items"}
                        </button>

                        {selectedItems.length >
                          0 && (
                          <button
                            type="button"
                            onClick={
                              handleAddItems
                            }
                            disabled={
                              addingItems
                            }
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-green-600 hover:bg-green-700 disabled:opacity-60"
                          >
                            {addingItems ? (
                              <FaCog className="animate-spin" />
                            ) : (
                              <FaPlus />
                            )}

                            Add (
                            {
                              selectedItems.length
                            }
                            ) to Grid
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* =============================================
                  SEARCH RESULT
              ============================================= */}

              {!isEditMode &&
                searchResults.length >
                  0 && (
                  <div
                    className={
                      cardClass
                    }
                  >
                    {/* HEADER */}

                    <div
                      className={`p-6 border-b ${
                        isDark
                          ? "border-gray-700"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                        />

                        <div>
                          <h3
                            className={`text-xl font-bold ${
                              isDark
                                ? "text-white"
                                : "text-gray-900"
                            }`}
                          >
                            Item Search
                            Results
                          </h3>

                          <p
                            className={`text-xs mt-1 ${
                              isDark
                                ? "text-gray-400"
                                : "text-gray-500"
                            }`}
                          >
                            Select the
                            items you
                            want to add
                          </p>
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {
                            searchResults.length
                          }{" "}
                          result(s)
                        </span>
                      </div>
                    </div>

                    {/* TABLE */}

                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="min-w-full w-full text-sm">
                        <thead
                          className={`sticky top-0 bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                        >
                          <tr>
                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap w-14">
                              Select
                            </th>

                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                              S.No
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                              Item Code /
                              Name
                            </th>
                          </tr>
                        </thead>

                        <tbody
                          className={`divide-y ${
                            isDark
                              ? "divide-gray-700"
                              : "divide-gray-200"
                          }`}
                        >
                          {searchResults.map(
                            (
                              item,
                              index
                            ) => {
                              const selected =
                                selectedItems.includes(
                                  item.itemcode
                                );

                              return (
                                <tr
                                  key={`${item.itemcode}-${index}`}
                                  onClick={() =>
                                    toggleSearchItem(
                                      item
                                    )
                                  }
                                  className={`cursor-pointer transition ${
                                    selected
                                      ? isDark
                                        ? `bg-${baseColor}-900/30`
                                        : `bg-${baseColor}-50`
                                      : tableRowClass
                                  }`}
                                >
                                  <td className="px-4 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={
                                        selected
                                      }
                                      readOnly
                                      className={`w-5 h-5 accent-${baseColor}-600 cursor-pointer`}
                                    />
                                  </td>

                                  <td
                                    className={`${tableCellClass} text-center`}
                                  >
                                    {index +
                                      1}
                                  </td>

                                  <td
                                    className={
                                      tableCellClass
                                    }
                                  >
                                    <div className="flex items-center gap-2">
                                      <FaBoxes
                                        className={`text-${baseColor}-500`}
                                      />

                                      <span className="font-medium">
                                        {
                                          item.display
                                        }
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* =============================================
                  STORE REQUEST INFO
              ============================================= */}

              {!isEditMode &&
                issueType ===
                  "FR" &&
                storeReqGrid.length >
                  0 && (
                  <div
                    className={
                      cardClass
                    }
                  >
                    <div
                      className={`p-6 border-b ${
                        isDark
                          ? "border-gray-700"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                        />

                        <div>
                          <h3
                            className={`text-xl font-bold ${
                              isDark
                                ? "text-white"
                                : "text-gray-900"
                            }`}
                          >
                            Store
                            Requests
                          </h3>

                          <p
                            className={`text-xs mt-1 ${
                              isDark
                                ? "text-gray-400"
                                : "text-gray-500"
                            }`}
                          >
                            Available
                            store request
                            records
                          </p>
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {
                            storeReqGrid.length
                          }{" "}
                          records
                        </span>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full w-full text-sm">
                        <thead
                          className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                        >
                          <tr>
                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                              S.No
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                              Request No
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                              Date
                            </th>
                          </tr>
                        </thead>

                        <tbody
                          className={`divide-y ${
                            isDark
                              ? "divide-gray-700"
                              : "divide-gray-200"
                          }`}
                        >
                          {storeReqGrid.map(
                            (
                              row,
                              index
                            ) => (
                              <tr
                                key={
                                  row.fld_StoreRequestid ||
                                  index
                                }
                                className={
                                  tableRowClass
                                }
                              >
                                <td
                                  className={`${tableCellClass} text-center`}
                                >
                                  {index +
                                    1}
                                </td>

                                <td
                                  className={
                                    tableCellClass
                                  }
                                >
                                  {row.fld_StoreRequestNo ||
                                    row.requestNo ||
                                    "-"}
                                </td>

                                <td
                                  className={
                                    tableCellClass
                                  }
                                >
                                  {row.ReqDate ||
                                    row.requestDate ||
                                    "-"}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* =============================================
                  ITEMS GRID
              ============================================= */}

              <div
                className={
                  cardClass
                }
              >
                {/* HEADER */}

                <div
                  className={`p-6 border-b ${
                    isDark
                      ? "border-gray-700"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                      />

                      <div>
                        <h3
                          className={`text-xl font-bold ${
                            isDark
                              ? "text-white"
                              : "text-gray-900"
                          }`}
                        >
                          Items to Issue
                        </h3>

                        <p
                          className={`text-xs mt-1 ${
                            isDark
                              ? "text-gray-400"
                              : "text-gray-500"
                          }`}
                        >
                          Enter request
                          and issue
                          quantities
                        </p>
                      </div>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {
                          gridItems.length
                        }{" "}
                        item(s)
                      </span>
                    </div>

                    {gridItems.length >
                      0 && (
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                            isDark
                              ? "bg-emerald-900/30 text-emerald-300"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          Total Issue
                          Qty:{" "}
                          {
                            totalIssueQty
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* TABLE / EMPTY */}

                {gridItems.length >
                0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-[1300px] w-full text-sm">
                        <thead
                          className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                        >
                          <tr>
                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                              #
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                              Item Code
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                              Item Name
                            </th>

                            <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                              Available
                              Qty
                            </th>

                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                              Req Qty
                            </th>

                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                              Issue Qty *
                            </th>

                            {issueFor ===
                              "D" && (
                              <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                                Department
                                Code *
                              </th>
                            )}

                            {issueFor ===
                              "V" && (
                              <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                                Vehicle
                                Code *
                              </th>
                            )}

                            {!isEditMode && (
                              <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                                Remove
                              </th>
                            )}
                          </tr>
                        </thead>

                        <tbody
                          className={`divide-y ${
                            isDark
                              ? "divide-gray-700"
                              : "divide-gray-200"
                          }`}
                        >
                          {gridItems.map(
                            (
                              row,
                              index
                            ) => (
                              <tr
                                key={`${row.fld_itemcode}-${index}`}
                                className={
                                  tableRowClass
                                }
                              >
                                {/* NUMBER */}

                                <td
                                  className={`${tableCellClass} text-center`}
                                >
                                  {index +
                                    1}
                                </td>

                                {/* CODE */}

                                <td
                                  className={
                                    tableCellClass
                                  }
                                >
                                  <span
                                    className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold font-mono ${
                                      isDark
                                        ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                        : `bg-${baseColor}-50 text-${baseColor}-700`
                                    }`}
                                  >
                                    {row.fld_itemcode ||
                                      "-"}
                                  </span>
                                </td>

                                {/* NAME */}

                                <td
                                  className={
                                    tableCellClass
                                  }
                                >
                                  <div className="flex items-center gap-2">
                                    <FaBoxes
                                      className={`text-${baseColor}-500`}
                                    />

                                    <span className="font-medium">
                                      {row.fld_itemname ||
                                        "-"}
                                    </span>
                                  </div>
                                </td>

                                {/* AVAILABLE */}

                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <span
                                    className={`inline-flex min-w-[70px] justify-end px-3 py-1.5 rounded-lg font-semibold ${
                                      isDark
                                        ? "bg-emerald-900/30 text-emerald-300"
                                        : "bg-emerald-50 text-emerald-700"
                                    }`}
                                  >
                                    {
                                      row.availableQty
                                    }
                                  </span>
                                </td>

                                {/* REQUEST QTY */}

                                <td className="px-3 py-3">
                                  <input
                                    type="number"
                                    min="0"
                                    value={
                                      row.requestQty
                                    }
                                    onChange={(
                                      event
                                    ) =>
                                      updateGridRow(
                                        index,
                                        "requestQty",
                                        event
                                          .target
                                          .value
                                      )
                                    }
                                    className={`${inputClass} !w-24`}
                                  />
                                </td>

                                {/* ISSUE QTY */}

                                <td className="px-3 py-3">
                                  <input
                                    type="number"
                                    min="0"
                                    value={
                                      row.issueQty
                                    }
                                    onChange={(
                                      event
                                    ) =>
                                      updateGridRow(
                                        index,
                                        "issueQty",
                                        event
                                          .target
                                          .value
                                      )
                                    }
                                    className={`${inputClass} !w-24`}
                                  />
                                </td>

                                {/* DEPARTMENT */}

                                {issueFor ===
                                  "D" && (
                                  <td className="px-3 py-3">
                                    <select
                                      value={row.departmentCode || ""}
                                      onChange={(event) =>
                                        handleRowCodeSelect(
                                          index,
                                          event.target.value
                                        )
                                      }
                                      className={`${inputClass} !min-w-[220px]`}
                                    >
                                      <option value="">
                                        Select Department
                                      </option>
                                      {renderCodeOptions(
                                        row.departmentCode,
                                        row.departmentName
                                      )}
                                    </select>
                                  </td>
                                )}

                                {/* VEHICLE */}

                                {issueFor ===
                                  "V" && (
                                  <td className="px-3 py-3">
                                    <select
                                      value={row.vehicleCode || ""}
                                      onChange={(event) =>
                                        handleRowCodeSelect(
                                          index,
                                          event.target.value
                                        )
                                      }
                                      className={`${inputClass} !min-w-[220px]`}
                                    >
                                      <option value="">
                                        {vehicleType === "H"
                                          ? "Select Hire Vehicle"
                                          : "Select Own Vehicle"}
                                      </option>
                                      {renderCodeOptions(
                                        row.vehicleCode,
                                        row.vehicleName
                                      )}
                                    </select>
                                  </td>
                                )}

                                {/* REMOVE */}

                                {!isEditMode && (
                                  <td className="px-4 py-3 text-center">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeGridRow(
                                          index
                                        )
                                      }
                                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow transition"
                                      title="Remove Item"
                                    >
                                      <FaTrash
                                        size={
                                          13
                                        }
                                      />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* FOOTER */}

                    <div
                      className={`p-6 border-t flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                        isDark
                          ? "border-gray-700 bg-gray-900/20"
                          : "border-gray-200 bg-gray-50/50"
                      }`}
                    >
                      <div className="flex flex-wrap gap-3">
                        <span
                          className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          Total Items:{" "}
                          {
                            gridItems.length
                          }
                        </span>

                        <span
                          className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                            isDark
                              ? "bg-emerald-900/30 text-emerald-300"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          Total Issue Qty:{" "}
                          {
                            totalIssueQty
                          }
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={
                          handleSubmit
                        }
                        disabled={
                          saving
                        }
                        className={`inline-flex items-center gap-2 px-7 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60`}
                      >
                        {saving ? (
                          <FaCog className="animate-spin" />
                        ) : (
                          <FaSave />
                        )}

                        {saving
                          ? "Saving..."
                          : isEditMode
                          ? "Update Store Issue"
                          : "Save Store Issue"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-14">
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                        isDark
                          ? "bg-gray-800"
                          : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-8 h-8 text-gray-400" />
                    </div>

                    <h3
                      className={`font-semibold ${
                        isDark
                          ? "text-gray-200"
                          : "text-gray-800"
                      }`}
                    >
                      No Items Added
                    </h3>

                    <p
                      className={`text-sm mt-1 ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      Search and select
                      items to populate
                      the Store Issue
                      grid.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ===================================================
            TOAST
        =================================================== */}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={
            isDark
              ? "dark"
              : "light"
          }
        />
      </LayoutWrapper>
    );
  };

export default StoreIssueNoteAdd;